import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import DataTable from '../components/ui/DataTable';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import ModalConfirm from '../components/ModalConfirm';
import NotFound from './NotFound';
import { toast } from '../components/Toast';
import { fmtInt, fmtBRL, fmtDataHora } from '../utils/format';

const USUARIOS_POR_PAGINA = 5;

export default function Admin() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [naoAutorizado, setNaoAutorizado] = useState(false); // conta sem acesso ao painel
  const [liberando, setLiberando] = useState(null); // fazenda a confirmar liberação
  const [paginaUsuarios, setPaginaUsuarios] = useState(1); // paginação da lista de usuários

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setDados(await api.getAdminDashboard());
    } catch (e) {
      // O backend responde 404 para quem não é o dono da plataforma: a página
      // deve simplesmente não existir para essa conta (não um erro de carga).
      if (e.status === 404) {
        setNaoAutorizado(true);
      } else {
        setErro(e.message || 'Não foi possível carregar o painel.');
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Conta sem permissão: renderiza a própria página 404, como pedido.
  if (naoAutorizado) return <NotFound />;

  // Paginação da lista de usuários (5 em 5), feita no cliente.
  const todosUsuarios = dados?.ultimosUsuarios || [];
  const totalPaginasUsuarios = Math.max(1, Math.ceil(todosUsuarios.length / USUARIOS_POR_PAGINA));
  const paginaAtual = Math.min(paginaUsuarios, totalPaginasUsuarios);
  const inicioUsuarios = (paginaAtual - 1) * USUARIOS_POR_PAGINA;
  const usuariosPagina = todosUsuarios.slice(inicioUsuarios, inicioUsuarios + USUARIOS_POR_PAGINA);

  const confirmarLiberacao = async () => {
    const f = liberando;
    setLiberando(null);
    try {
      const r = await api.liberarFazendaUmMes(f.id);
      toast(`${f.nome} liberada até ${new Date(r.assinaturaAte + 'T00:00:00').toLocaleDateString('pt-BR')}.`, 'success');
      carregar();
    } catch (e) {
      toast(e.message || 'Não foi possível liberar o acesso.', 'error');
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Administração"
        icone="usuarios"
        descricao="Painel interno da plataforma"
      />
      <div className="card">
        <Estado carregando={carregando} erro={erro} aoTentarNovamente={carregar} skeleton="card" itens={4}>
          {dados && (
            <>
              <MetricaGrid min={160}>
                <Metrica label="Usuários cadastrados" value={fmtInt(dados.totalUsuarios)} destaque />
                <Metrica label="Contas vencidas" value={fmtInt((dados.vencidas || []).length)} />
                <Metrica label="Valor a receber" value={fmtBRL(dados.totalReceber || 0)} destaque />
              </MetricaGrid>

              {/* Usuários cadastrados — todos, paginados de 5 em 5 */}
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  Usuários cadastrados
                  {todosUsuarios.length > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {todosUsuarios.length}</span>
                  )}
                </h3>
                <DataTable
                  densa
                  linhas={usuariosPagina}
                  chaveDe={(u, i) => `${inicioUsuarios + i}`}
                  colunas={[
                    { chave: 'nome', titulo: 'Nome', papel: 'titulo', render: u => u.nome },
                    { chave: 'email', titulo: 'Email', papel: 'subtitulo', render: u => u.email || '' },
                    { chave: 'fazenda', titulo: 'Fazenda', render: u => u.fazenda || '' },
                    { chave: 'ultimoLogin', titulo: 'Último login', render: u => (
                      u.ultimoLogin
                        ? fmtDataHora(u.ultimoLogin)
                        : <span style={{ color: 'var(--text-muted)' }}>Nunca</span>
                    ) },
                  ]}
                  vazio={{ icone: 'usuarios', titulo: 'Nenhum usuário' }}
                />

                {totalPaginasUsuarios > 1 && (
                  <div className="paginacao">
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => setPaginaUsuarios(p => Math.max(1, p - 1))}
                      disabled={paginaAtual <= 1}
                    >
                      Anterior
                    </button>
                    <span className="paginacao-info">
                      Página {paginaAtual} de {totalPaginasUsuarios}
                    </span>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => setPaginaUsuarios(p => Math.min(totalPaginasUsuarios, p + 1))}
                      disabled={paginaAtual >= totalPaginasUsuarios}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </Estado>
      </div>

      {dados && (
        <>
          {/* Contas já vencidas — bloqueadas, aguardando pagamento */}
          <div className="card">
            <h2>Contas vencidas (aguardando pagamento)</h2>
            <DataTable
              densa
              linhas={dados.vencidas || []}
              chaveDe={f => f.id}
              colunas={[
                { chave: 'nome', titulo: 'Fazenda', papel: 'titulo', render: f => f.nome },
                { chave: 'plano', titulo: 'Plano', papel: 'subtitulo', render: f => f.plano },
                { chave: 'venceuEm', titulo: 'Venceu em', render: f => (
                  <span>
                    {new Date(f.venceuEm + 'T00:00:00').toLocaleDateString('pt-BR')}
                    <span style={{ color: '#c53030', fontWeight: 600 }}> · há {f.diasVencido} dia{f.diasVencido !== 1 ? 's' : ''}</span>
                  </span>
                ) },
                { chave: 'valorDevido', titulo: 'A receber', alinhar: 'direita', papel: 'destaque', render: f => (
                  <span title={f.mesesDevendo > 1 ? `${f.mesesDevendo} meses × ${fmtBRL(f.valorMensal)}` : undefined}>
                    {fmtBRL(f.valorDevido)}
                  </span>
                ) },
                { chave: 'acao', titulo: '', alinhar: 'direita', render: f => (
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={() => setLiberando(f)}
                  >
                    Liberar 1 mês
                  </button>
                ) },
              ]}
              vazio={{ icone: 'concluido', titulo: 'Nenhuma conta vencida' }}
            />
          </div>

          {/* Trial vencendo */}
          <div className="card">
            <h2>Trial vencendo nos próximos 5 dias</h2>
            <DataTable
              densa
              linhas={dados.trialVencendo || []}
              chaveDe={f => f.id}
              colunas={[
                { chave: 'nome', titulo: 'Fazenda', papel: 'titulo', render: f => f.nome },
                { chave: 'plano', titulo: 'Plano', papel: 'subtitulo', render: f => (
                  <span>{f.plano}{f.valorMensal != null ? ` · ${fmtBRL(f.valorMensal)}/mês` : ''}</span>
                ) },
                { chave: 'trialAte', titulo: 'Vence em', render: f => f.trialAte },
                { chave: 'dias', titulo: 'Dias restantes', alinhar: 'direita', papel: 'destaque', render: f => (
                  <span style={{ color: f.diasRestantes <= 2 ? '#c53030' : '#975a16', fontWeight: 600 }}>
                    {f.diasRestantes} dia{f.diasRestantes !== 1 ? 's' : ''}
                  </span>
                ) },
                { chave: 'acao', titulo: '', alinhar: 'direita', render: f => (
                  <button type="button" className="btn-primary btn-sm" onClick={() => setLiberando(f)}>
                    Liberar 1 mês
                  </button>
                ) },
              ]}
              vazio={{ icone: 'concluido', titulo: 'Nenhuma fazenda com trial vencendo' }}
            />
          </div>

          {/* Pagamento vencendo */}
          <div className="card">
            <h2>Pagamento vencendo nos próximos 5 dias</h2>
            <DataTable
              densa
              linhas={dados.pagamentoVencendo || []}
              chaveDe={f => f.id}
              colunas={[
                { chave: 'nome', titulo: 'Fazenda', papel: 'titulo', render: f => f.nome },
                { chave: 'plano', titulo: 'Plano', papel: 'subtitulo', render: f => (
                  <span>{f.plano}{f.valorMensal != null ? ` · ${fmtBRL(f.valorMensal)}/mês` : ''}</span>
                ) },
                { chave: 'assinaturaAte', titulo: 'Vence em', render: f => f.assinaturaAte },
                { chave: 'dias', titulo: 'Dias restantes', alinhar: 'direita', papel: 'destaque', render: f => (
                  <span style={{ color: f.diasRestantes <= 2 ? '#c53030' : '#975a16', fontWeight: 600 }}>
                    {f.diasRestantes} dia{f.diasRestantes !== 1 ? 's' : ''}
                  </span>
                ) },
                { chave: 'acao', titulo: '', alinhar: 'direita', render: f => (
                  <button type="button" className="btn-primary btn-sm" onClick={() => setLiberando(f)}>
                    Liberar 1 mês
                  </button>
                ) },
              ]}
              vazio={{ icone: 'concluido', titulo: 'Nenhuma fazenda com pagamento vencendo' }}
            />
          </div>
        </>
      )}

      {liberando && (
        <ModalConfirm
          titulo="Liberar acesso por 1 mês"
          mensagem={
            <>
              Confirmar o recebimento e liberar <strong>{liberando.nome}</strong> por 1 mês?
              {liberando.valorDevido != null ? (
                <span style={{ display: 'block', marginTop: '0.4rem', color: 'var(--text-muted)' }}>
                  Valor a receber: <strong>{fmtBRL(liberando.valorDevido)}</strong>
                </span>
              ) : liberando.valorMensal != null && (
                <span style={{ display: 'block', marginTop: '0.4rem', color: 'var(--text-muted)' }}>
                  Plano <strong>{liberando.plano}</strong> · mensalidade <strong>{fmtBRL(liberando.valorMensal)}</strong>
                </span>
              )}
            </>
          }
          labelConfirmar="Liberar 1 mês"
          onConfirm={confirmarLiberacao}
          onCancel={() => setLiberando(null)}
        />
      )}
    </div>
  );
}
